import React, { useState, useEffect } from 'react';
import { getPageBySlug, getGalleryBySlug } from '../lib/cms';
import { useSEO } from '../hooks/useSEO';

export default function Bread() {
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    async function loadData() {
      const [page, gallery] = await Promise.all([
        getPageBySlug('broed'),
        getGalleryBySlug('broed')
      ]);
      setPageData(page);
      setGalleryData(gallery);
      setLoading(false);
    }
    loadData();
  }, []);

  useSEO({
    title: pageData?.meta_title || 'Brød & morgenbrød · Vissenbjerg Bageri',
    description: pageData?.meta_description || 'Friskbagt brød hver dag hos Vissenbjerg Bageri. Vi tilbyder langtidshævede surdejsbrød, stenovnsbrød, frisksmurte sandwich og glutenreduceret brød.'
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-cream">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold-brown border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-transparent text-chocolate py-12 md:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-brown">
            Vores Bagværk
          </span>
          <h1 className="font-serif text-4xl font-bold sm:text-5xl text-chocolate">
            Brød & Morgenbrød
          </h1>
          <div className="h-1 w-20 bg-gold-brown mx-auto rounded-full" />
        </div>

        {/* Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="space-y-6">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate">
              Friskbagt brød hver dag – hele dagen
            </h2>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vissenbjerg bageri er din lokale leverandør af dagligt friskbagt brød. Den første medarbejder møder ind klokken 01.30 for at starte dagens brødproduktion, og vi bliver ved med at bage, indtil den sidste går klokken 15:30. Det betyder, at du som kunde er garanteret et lunt og friskbagt brød gennem hele dagen.
            </p>
            <h3 className="font-serif text-xl font-bold text-chocolate pt-2">
              Langtidshævede surdejsbrød & stenovnsbrød
            </h3>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vi har et stort og bredt udvalg af brød i alle afskygninger i form af klassiske franskbrød og rugbrød med smag og substans samt rundstykker og fiberrige grovboller til enhver smag.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Hos Vissenbjerg bageri er vi særligt populære på grund af vores langtidshævede surdejsbrød og stenovnsbrød, hvorfor 90 procent af vores brødudvalg består af denne type brød og grovstykker. Vores surdejsbrød og stenovnsbrød er alle langsomt koldhævet i 24 timer og derefter bagt i en glohed stenovn. Det giver den helt rigtige udvikling af surdejskulturen og brødet – og derfor meget mere smag, en saftigere krumme, sprød skorpe og ikke mindst en bedre friskholdning.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gold-brown/10 h-[450px] relative">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/img-9315.jpg"
              alt="Friskbagt surdejsbrød og stenovnsbrød"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Extra Info (Sandwich & Glutenreduceret) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          <div className="rounded-2xl bg-white p-8 shadow-md border border-gold-brown/10 space-y-4">
            <div className="h-12 w-12 rounded-full bg-beige text-gold-brown flex items-center justify-center">
              <i className="fa-solid fa-utensils text-xl" />
            </div>
            <h3 className="font-serif text-xl font-bold text-chocolate">Frisksmurte sandwich</h3>
            <p className="text-sm text-chocolate/70 leading-relaxed">
              Havde du ikke lige tid til at smøre en madpakke i farten på vej ud af døren, eller står du og mangler noget lækkert at byde kundebesøget i virksomheden, så er vores lækre frisksmurte sandwich altid en god løsning.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-md border border-gold-brown/10 space-y-4">
            <div className="h-12 w-12 rounded-full bg-beige text-gold-brown flex items-center justify-center">
              <i className="fa-solid fa-wheat-awn-circle-exclamation text-xl" />
            </div>
            <h3 className="font-serif text-xl font-bold text-chocolate">Glutenreduceret</h3>
            <p className="text-sm text-chocolate/70 leading-relaxed">
              Som noget nyt tilbyder vi nu også glutenreduceret brød. Kontakt os gerne i butikken eller pr. telefon for mere information omkring vores glutenreducerede udvalg og ingredienser.
            </p>
          </div>
        </div>

        {/* Technical Connected Gallery */}
        {galleryData && galleryData.images && galleryData.images.length > 0 && (
          <div className="space-y-8">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <h2 className="font-serif text-3xl font-bold text-chocolate">Brødgalleri</h2>
              <p className="text-sm text-chocolate/60">
                Se et udvalg af vores friskbagte brød, rundstykker og specialiteter. Klik på et billede for at se det i stor visning.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryData.images.map((imgUrl, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImage(imgUrl)}
                  className="group relative h-64 sm:h-80 md:h-96 overflow-hidden rounded-xl bg-chocolate border border-gold-brown/10 focus:outline-none focus:ring-2 focus:ring-gold-brown"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,height=800,fit=cover,quality=85,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`Brød galleri ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-chocolate/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-cream text-2xl drop-shadow-md" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Lightbox / Stor visning */}
        {activeImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-chocolate/95 p-4 backdrop-blur-sm animate-fadeIn"
            onClick={() => setActiveImage(null)}
          >
            <button
              onClick={() => setActiveImage(null)}
              className="absolute top-4 right-4 text-cream/80 hover:text-white text-3xl focus:outline-none"
              aria-label="Luk visning"
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div
              className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={activeImage}
                alt="Stor visning"
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
